const SELECTOR_ORIGIN_PATH_STATE_KEY = "presentationAnalyticsSelectorOrigin";
const SELECTOR_ENTRY_ID_STATE_KEY = "presentationAnalyticsSelectorEntryId";
const MAX_SELECTOR_SNAPSHOTS = 20;

let nextSelectorEntryId = 0;

export interface PresentationAnalyticsSelectorOrigin {
  selectorPath: string;
  selectorEntryId: string;
}

export interface PresentationAnalyticsSelectorSnapshot<TResult> {
  results: TResult[];
  hasMore: boolean;
  eligibleDocumentsAvailable?: boolean;
  scrollTop: number;
}

interface StoredPresentationAnalyticsSelectorSnapshot<TResult> {
  selectorPath: string;
  snapshot: PresentationAnalyticsSelectorSnapshot<TResult>;
}

const selectorSnapshots = new Map<
  string,
  StoredPresentationAnalyticsSelectorSnapshot<unknown>
>();

export function isPresentationAnalyticsSelectorPath(path: string): boolean {
  return /^\/u\/analytics\/presentation_analytics(?:[?#]|$)/.test(path);
}

function readStateString(state: unknown, key: string): string | null {
  if (!state || typeof state !== "object") {
    return null;
  }

  const value = (state as Record<string, unknown>)[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function readPresentationAnalyticsSelectorEntryId(
  state: unknown,
): string | null {
  return readStateString(state, SELECTOR_ENTRY_ID_STATE_KEY);
}

export function presentationAnalyticsSelectorEntryState(
  selectorEntryId: string,
): Record<string, string> {
  return selectorEntryId
    ? { [SELECTOR_ENTRY_ID_STATE_KEY]: selectorEntryId }
    : {};
}

export function ensurePresentationAnalyticsSelectorEntryId(): string {
  const existingEntryId = readPresentationAnalyticsSelectorEntryId(
    window.history.state,
  );
  if (existingEntryId) {
    return existingEntryId;
  }

  const selectorEntryId = `presentation-analytics-${Date.now().toString(36)}-${(++nextSelectorEntryId).toString(
    36,
  )}`;
  const currentState =
    window.history.state && typeof window.history.state === "object"
      ? window.history.state
      : {};

  window.history.replaceState(
    {
      ...currentState,
      ...presentationAnalyticsSelectorEntryState(selectorEntryId),
    },
    "",
  );

  return selectorEntryId;
}

export function buildPresentationAnalyticsOriginState(
  selectorPath: string,
  selectorEntryId: string,
): Record<string, string> {
  return isPresentationAnalyticsSelectorPath(selectorPath) && selectorEntryId
    ? {
        [SELECTOR_ORIGIN_PATH_STATE_KEY]: selectorPath,
        [SELECTOR_ENTRY_ID_STATE_KEY]: selectorEntryId,
      }
    : {};
}

export function readPresentationAnalyticsSelectorOrigin(
  state: unknown,
): PresentationAnalyticsSelectorOrigin | null {
  const selectorPath = readStateString(state, SELECTOR_ORIGIN_PATH_STATE_KEY);
  const selectorEntryId = readPresentationAnalyticsSelectorEntryId(state);

  return selectorPath &&
    selectorEntryId &&
    isPresentationAnalyticsSelectorPath(selectorPath)
    ? { selectorPath, selectorEntryId }
    : null;
}

export function presentationAnalyticsOriginState(
  state: unknown,
): Record<string, string> | undefined {
  const origin = readPresentationAnalyticsSelectorOrigin(state);
  return origin
    ? buildPresentationAnalyticsOriginState(
        origin.selectorPath,
        origin.selectorEntryId,
      )
    : undefined;
}

export function savePresentationAnalyticsSelectorSnapshot<TResult>(
  selectorEntryId: string,
  selectorPath: string,
  snapshot: PresentationAnalyticsSelectorSnapshot<TResult>,
): void {
  if (!selectorEntryId || !isPresentationAnalyticsSelectorPath(selectorPath)) {
    return;
  }

  selectorSnapshots.delete(selectorEntryId);
  selectorSnapshots.set(selectorEntryId, {
    selectorPath,
    snapshot: snapshot as PresentationAnalyticsSelectorSnapshot<unknown>,
  });

  while (selectorSnapshots.size > MAX_SELECTOR_SNAPSHOTS) {
    const oldestKey = selectorSnapshots.keys().next().value;
    if (typeof oldestKey !== "string") {
      break;
    }
    selectorSnapshots.delete(oldestKey);
  }
}

export function takePresentationAnalyticsSelectorSnapshot<TResult>(
  selectorEntryId: string,
  selectorPath: string,
): PresentationAnalyticsSelectorSnapshot<TResult> | null {
  const storedSnapshot = selectorSnapshots.get(selectorEntryId);
  if (!storedSnapshot) {
    return null;
  }

  selectorSnapshots.delete(selectorEntryId);
  return storedSnapshot.selectorPath === selectorPath
    ? (storedSnapshot.snapshot as PresentationAnalyticsSelectorSnapshot<TResult>)
    : null;
}

export function clearPresentationAnalyticsSelectorSnapshots(): void {
  selectorSnapshots.clear();
}
